// Lightweight programmatic toast for surfacing async-fetch outcomes
// without resorting to window.alert (CSP-blocked in prod, ugly on iOS,
// no theming). Mirrors the visual pattern in discover_controller's
// inline toast — pinned to the top-center, auto-dismissing, frosted
// pill — but parameterised so success and error read distinctly.
//
// Usage:
//   import { showToast } from "../helpers/toast"
//   showToast("Saved!")                            // success (default)
//   showToast("Couldn't save", { variant: "error" })

const VARIANTS = {
  success: {
    ring: "ring-warm-200 dark:ring-gray-700",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />',
    iconColor: "text-warm-500"
  },
  error: {
    ring: "ring-red-200 dark:ring-red-900/50",
    icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />',
    iconColor: "text-red-500"
  }
}

export function showToast(message, { variant = "success", duration = 3000 } = {}) {
  // Replace any existing programmatic toast so a rapid error → success
  // sequence doesn't pile up.
  document.querySelector("[data-programmatic-toast]")?.remove()

  const cfg = VARIANTS[variant] || VARIANTS.success
  const toast = document.createElement("div")
  toast.setAttribute("data-programmatic-toast", "")
  // Announce to screen readers: errors interrupt (assertive), successes wait
  // for a pause (polite). Without this a silent fetch failure is invisible to
  // VoiceOver — the exact failure this toast exists to surface.
  toast.setAttribute("role", variant === "error" ? "alert" : "status")
  toast.setAttribute("aria-live", variant === "error" ? "assertive" : "polite")
  toast.className = [
    "fixed top-[max(1rem,env(safe-area-inset-top))] left-1/2 -translate-x-1/2 z-[70]",
    "rounded-full bg-white/95 dark:bg-gray-800/95 backdrop-blur-md",
    "px-5 py-2.5 shadow-lg ring-1",
    cfg.ring,
    "text-sm font-medium text-gray-900 dark:text-white",
    "flex items-center gap-2 max-w-[calc(100vw-2rem)]",
    "opacity-0 translate-y-[-8px] transition-all duration-300"
  ].join(" ")
  // Append EMPTY first, fill on the next frame. A live region must already be
  // in the accessibility tree and then be MUTATED for VoiceOver to announce it —
  // a region inserted with its text already present is treated as initial
  // content and skipped (the same reason the offline banner + servings regions
  // pre-exist empty and are filled later). Filling pre-append silently dropped
  // the announcement this toast exists to make.
  document.body.appendChild(toast)

  requestAnimationFrame(() => {
    toast.innerHTML = `<svg class="h-4 w-4 ${cfg.iconColor} shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">${cfg.icon}</svg><span>${message}</span>`
    toast.style.opacity = "1"
    toast.style.transform = "translateX(-50%) translateY(0)"
  })

  setTimeout(() => {
    toast.style.opacity = "0"
    toast.style.transform = "translateX(-50%) translateY(-8px)"
    setTimeout(() => toast.remove(), 300)
  }, duration)
}
